import React, { useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { useQuery } from "react-query";
import { Terminal } from "lucide-react";
import { getApiActivityLogs, getTopApiEndpoints } from "services/apiActivityLog.service";
import { ApiActivityLogFilters } from "types/apiActivityLog.type";

const METHOD_OPTIONS = ["", "POST", "PUT", "PATCH", "DELETE"];

const methodBadge = (method: string) => {
  const tone: Record<string, string> = {
    POST: "bg-green-50 text-green-700",
    PUT: "bg-blue-50 text-blue-700",
    PATCH: "bg-amber-50 text-amber-700",
    DELETE: "bg-red-50 text-red-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${tone[method] ?? "bg-gray-100 text-gray-600"}`}>
      {method}
    </span>
  );
};

const statusTone = (status: number) =>
  status >= 500
    ? "text-red-600"
    : status >= 400
    ? "text-amber-600"
    : "text-green-600";

const ApiActivityLogs = () => {
  const [filters, setFilters] = useState<ApiActivityLogFilters>({});
  const [draft, setDraft] = useState<ApiActivityLogFilters>({});
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: topRes } = useQuery(["top-api-endpoints"], () => getTopApiEndpoints({}));
  const { data: logsRes, isLoading } = useQuery(
    ["api-activity-logs", filters, page],
    () => getApiActivityLogs({ page, limit }, filters)
  );

  const topEndpoints = topRes?.data ?? [];
  const logs = logsRes?.data?.data ?? [];
  const total = logsRes?.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const applyFilters = () => {
    setPage(1);
    setFilters(draft);
  };

  return (
    <React.Fragment>
      <BreadCrumb title="API Activity" pageTitle="API Activity" />

      {!!topEndpoints.length && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm mb-5">
          <p className="text-sm font-semibold text-gray-800 mb-4">Top endpoints by call volume</p>
          <div className="space-y-2">
            {topEndpoints.map((e) => {
              const max = topEndpoints[0]?.count || 1;
              return (
                <div key={`${e.method}:${e.path}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-mono text-gray-700 truncate pr-3">
                      {methodBadge(e.method)} <span className="ml-2">{e.path}</span>
                    </span>
                    <span className="text-gray-500 whitespace-nowrap">
                      {e.count} calls · {e.uniqueUsers} people
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-indigo-500"
                      style={{ width: `${Math.max(2, (e.count / max) * 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm mb-5">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">User ID</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="user id"
              value={draft.userId ?? ""}
              onChange={(e) => setDraft({ ...draft, userId: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Method</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={draft.method ?? ""}
              onChange={(e) => setDraft({ ...draft, method: e.target.value })}
            >
              {METHOD_OPTIONS.map((m) => (
                <option key={m} value={m}>{m || "Any"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Path starts with</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="/api/v1/jobs"
              value={draft.path ?? ""}
              onChange={(e) => setDraft({ ...draft, path: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="200 / 400 / 500"
              value={draft.status ?? ""}
              onChange={(e) => setDraft({ ...draft, status: e.target.value })}
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input
                type="date"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={draft.from ?? ""}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              />
            </div>
            <button
              onClick={applyFilters}
              className="self-end bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg px-4 py-2"
            >
              Filter
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="py-12 text-center text-sm text-gray-400">Loading activity…</div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Terminal size={22} className="text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">No API activity matches this filter</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">When</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Method</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Path</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((log) => (
                    <tr key={log._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleDateString()}, {new Date(log.createdAt).toLocaleTimeString()}
                      </td>
                      <td className="px-4 py-3">{methodBadge(log.method)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">{log.path}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {log.user
                          ? `${log.user.firstName ?? ""} ${log.user.lastName ?? ""}`.trim() || log.user.email
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{log.role ?? "—"}</td>
                      <td className={`px-4 py-3 font-semibold ${statusTone(log.status)}`}>{log.status}</td>
                      <td className="px-4 py-3 text-gray-500">{log.durationMs}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                Page {page} of {totalPages} · {total} total
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="text-sm text-indigo-600 hover:underline disabled:text-gray-300 disabled:no-underline"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="text-sm text-indigo-600 hover:underline disabled:text-gray-300 disabled:no-underline"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </React.Fragment>
  );
};

export default ApiActivityLogs;
